import React, {useState, useEffect, useRef} from 'react';
import {View, Text, TextInput, Pressable, Animated} from 'react-native';
import {DynamicEidaDescriptionProps} from '../../Types/ControlTypes';

import {IEidaService} from '../../Services/Interfaces/IEidaService';
import EidaService from '../../Services/EidaService';
import Colors from '../../Styles/Shared/Colors';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';

const DynamicEidaDescription = (
  props: DynamicEidaDescriptionProps,
): React.ReactElement => {
  const goodCharThreshold = props.config?.goodCharThreshold ?? 120;

  const [description, setDescription] = useState<string | null>(
    getStateFromProps(props.value, 'description'),
  );
  const [originalDescription, setOriginalDescription] = useState<string | null>(
    getStateFromProps(props.value, 'originalDescription'),
  );
  const [followUpQuestions, setFollowUpQuestions] = useState<string[] | null>(
    getStateFromProps(props.value, 'followUpQuestions'),
  );
  const [goodDescFlag, setGoodDescFlag] = useState<boolean | null>(
    getStateFromProps(props.value, 'goodDescFlag'),
  );
  const [qcTime, setQCTime] = useState<number | null>(
    getStateFromProps(props.value, 'qcTime'),
  );
  const [eidaCount, setEidaCount] = useState<number>(
    getStateFromProps(props.value, 'eidaCount') ?? 0,
  );
  const [eidaVersion, setEidaVersion] = useState<string | null>(
    getStateFromProps(props.value, 'eidaVersion'),
  );
  const [eidaError, setEidaError] = useState<string | null>(null);

  const [isAnalyzing, setIsAnalyzing] = useState<boolean | null>(null);
  const [isFocused, setIsFocused] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const [position, setPosition] = useState(0);

  const [inputMessage, setInputMessage] = useState<string | null>(null);
  const [inputStyle, setInputStyle] = useState<string | null>(null);

  const eidaService: IEidaService = new EidaService(() => {});

  const animatedHeight = useRef(new Animated.Value(0)).current;
  const [spinner, setSpinner] = useState<string | null>(null);

  useEffect(() => {
    //Every time description changes, clean the good description flag
    setGoodDescFlag(false);
  }, [description]);

  useEffect(() => {
    if (isAnalyzing) analyze();
    else saveState();

    getInputMessageAndStyle();

    //If isAnalyzing start the timeout animation, otherwise clean the spinner
    if (isAnalyzing)
      setTimeout(function () {
        setSpinner('.');
      }, 400);
    else setSpinner(null);
  }, [isAnalyzing]);

  useEffect(() => {
    //While isAnalyzing call the timeout animation
    if (isAnalyzing)
      setTimeout(function () {
        setSpinner(spinner != null && spinner.length < 3 ? spinner + '.' : '');
      }, 400);
  }, [spinner]);

  useEffect(() => {
    let toValue = 0;
    if (followUpQuestions && followUpQuestions.length > 0) toValue = 800;

    Animated.timing(animatedHeight, {
      toValue: toValue,
      duration: 1000,
      delay: 200,
      useNativeDriver: false,
    }).start();
  }, [followUpQuestions]);

  useEffect(() => {
    if (props.showError) validate();
  }, [props.showError]);

  function getStateFromProps(propsValue: string | undefined, state: string) {
    try {
      if (propsValue) {
        let value = JSON.parse(propsValue);

        if (state == 'description' && value.description)
          return value.description;

        if (state == 'originalDescription' && value.originalDescription)
          return value.originalDescription;

        if (
          state == 'followUpQuestions' &&
          value.followUpQuestions &&
          value.followUpQuestions.length > 0
        )
          return value.followUpQuestions;

        if (state == 'goodDescFlag' && value.goodDescFlag)
          return value.goodDescFlag;

        if (state == 'qcTime' && value.qcTime) return value.qcTime;

        if (state == 'eidaCount' && value.eidaCount) return value.eidaCount;

        if (state == 'eidaVersion' && value.eidaVersion)
          return value.eidaVersion;
      }
    } catch (error) {
      return null;
    }
  }

  function focus(): void {
    setIsFocused(true);

    if (props.onFocus) props.onFocus(position);
  }

  function blur(): void {
    setIsFocused(false);

    getInputMessageAndStyle();

    saveState();
  }

  function validate(): boolean {
    let isValid = true;
    let error = '';

    if (props.config?.required && !description) {
      isValid = false;
      error =
        (props.label ? '"' + props.label + '"' : 'This field') + ' is required';
    }

    if (props.showError || isFocused !== null) setError(error);

    return isValid;
  }

  function getInputMessageAndStyle(): void {
    if (isAnalyzing) {
      setInputMessage('EIDA is analyzing');
      setInputStyle(Colors.yellow);
    } else if (followUpQuestions && followUpQuestions.length > 0) {
      setInputMessage('');
      setInputStyle(Colors.yellow);
    } else if (goodDescFlag) {
      setInputMessage('Good description!');
      setInputStyle(Colors.green);
    } else if (eidaError) {
      setInputMessage(eidaError);
      setInputStyle(Colors.red);
    } else if (
      isFocused != null &&
      description &&
      description.length < goodCharThreshold
    ) {
      setInputMessage('Description too short');
      setInputStyle(Colors.red);
    } else {
      setInputMessage(null);
      setInputStyle(null);
    }
  }

  function performAnalysis(): void {
    if (eidaCount == 0) setOriginalDescription(description);

    setIsAnalyzing(true);

    if (props.onFocus) props.onFocus(position);
  }

  const analyze = async () => {
    let _followUpQuestions: string[] | null = null;
    let _goodDescFlag = null;
    let _error = null;

    let qualityControl = await eidaService.getQC({
      IncidentType: props.config?.incidentType!,
      Description: description ?? '',
    });

    if (qualityControl && !qualityControl.error) {
      setQCTime(qualityControl.time ?? null);
      setEidaCount(eidaCount + 1);
      setEidaVersion(qualityControl.version ?? null);

      if (
        qualityControl.followUpQuestions &&
        qualityControl.followUpQuestions.length > 0
      )
        _followUpQuestions = qualityControl.followUpQuestions;
      else _goodDescFlag = true;
    }

    if (qualityControl && qualityControl.error) _error = qualityControl.error;
    else if (!_followUpQuestions && !_goodDescFlag)
      //This is when API call failed
      _error = 'Analysis service temporarily offline';

    setFollowUpQuestions(_followUpQuestions);
    setGoodDescFlag(_goodDescFlag);
    setEidaError(_error);

    setIsAnalyzing(false);
  };

  function saveState(): void {
    let isValid = validate();

    let value = JSON.stringify({
      description: description,
      originalDescription: originalDescription,
      followUpQuestions: followUpQuestions,
      goodDescFlag: goodDescFlag,
      qcTime: qcTime,
      eidaCount: eidaCount,
      version: eidaVersion,
    });

    if (props.onChange && props.value != value)
      props.onChange(
        props.controlId,
        props.controlTypeId,
        value,
        isValid,
        null,
        null,
      );
  }

  return (
    <View
      style={{
        display: props.visible === false ? 'none' : 'flex',
        marginTop: 6,
      }}
      onLayout={event => setPosition(event.nativeEvent.layout.y)}>
      <Animated.View
        style={{
          display:
            followUpQuestions && followUpQuestions.length > 0 ? 'flex' : 'none',
          maxHeight: animatedHeight,
          overflow: 'hidden',
        }}>
        <Text
          style={[
            ControlsStyleSheet.label,
            {textTransform: 'none', letterSpacing: 1, fontSize: 14},
          ]}>
          What's Missing?
        </Text>
        <View
          style={{
            backgroundColor: Colors.lightYellow,
            gap: 6,
            borderRadius: 12,
            borderWidth: 2,
            borderColor: Colors.yellow,
            padding: 12,
            marginTop: 12,
          }}>
          {followUpQuestions &&
            followUpQuestions.length > 0 &&
            followUpQuestions.map((question, i) => (
              <View key={i} style={{flexDirection: 'row', gap: 4}}>
                <Text
                  style={[
                    ControlsStyleSheet.label,
                    {
                      fontSize: 13,
                      marginTop: 0,
                      marginBottom: 0,
                    },
                  ]}>
                  {'\u2022'}
                </Text>
                <Text
                  style={[
                    ControlsStyleSheet.label,
                    {
                      fontSize: 13,
                      textTransform: 'none',
                      marginTop: 0,
                      marginBottom: 0,
                      letterSpacing: 0.8,
                    },
                  ]}>
                  {question}
                </Text>
              </View>
            ))}
        </View>
      </Animated.View>
      {props.label ? (
        <Text style={ControlsStyleSheet.label}>
          {props.label}
          <Text style={ControlsStyleSheet.required}>
            {props.config?.required ? '*' : ''}
          </Text>
        </Text>
      ) : null}
      <View>
        <TextInput
          multiline={true}
          style={[
            ControlsStyleSheet.input,
            ControlsStyleSheet.reportDescription,
            isFocused && ControlsStyleSheet.inputFocused,
            {marginBottom: 18},
            inputStyle != null && {
              borderWidth: 2,
              borderColor: inputStyle,
            },
          ]}
          placeholder={props.config?.placeholder}
          placeholderTextColor={ControlsStyleSheet.placeholder.color}
          value={description ?? ''}
          onChangeText={newText => setDescription(newText)}
          onFocus={() => focus()}
          onBlur={() => blur()}
          editable={!props.disabled}
        />
        {inputMessage ? (
          <View
            style={[
              ControlsStyleSheet.reportDescriptionFrame,
              {
                bottom: 4,
                borderRadius: 6,
                paddingHorizontal: 6,
                flexDirection: 'row',
              },
              inputStyle != null && {
                backgroundColor: inputStyle,
              },
            ]}>
            <Text
              style={{
                color: Colors.white,
                fontSize: 14,
              }}>
              {inputMessage}
            </Text>
            {isAnalyzing ? (
              <Text style={{color: Colors.white, fontSize: 14, minWidth: 14}}>
                {spinner}
              </Text>
            ) : null}
          </View>
        ) : null}
      </View>
      <View style={{flexDirection: 'row', justifyContent: 'space-between'}}>
        <Text style={[ControlsStyleSheet.error, {marginTop: 0}]}>{error}</Text>
        {!goodDescFlag ? (
          <Pressable
            style={({pressed}) => [
              CommonStyleSheet.greenButton,
              {marginTop: 0},
              pressed && {
                opacity: 0.8,
              },
              followUpQuestions &&
                followUpQuestions.length > 0 && {
                  backgroundColor: Colors.yellow,
                },
              isAnalyzing && {
                backgroundColor: Colors.gray,
              },
            ]}
            disabled={isAnalyzing}
            onPress={() => performAnalysis()}>
            <Text style={CommonStyleSheet.greenButtonText}>
              {followUpQuestions && followUpQuestions.length > 0
                ? 'Reanalyze!'
                : 'Analyze!'}
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
};

export default DynamicEidaDescription;
